/**
 * Realtime channel of the notification center (#1090).
 *
 * The database (cdb_tqs) is the source of truth; a push is only a signal.
 * Every (re)connect calls `onConnected`, and the subscriber re-syncs from the
 * REST API — whatever happened while the tab was offline or the laptop slept
 * shows up without a reload.
 *
 * Transport: Azure Web PubSub (`@azure/web-pubsub-client`, reliable JSON
 * subprotocol, auto-reconnect). The BFF's negotiate endpoint hands out a signed
 * URL, a fresh one for every connect.
 *
 * Message contract: `{event_type, notification, reason?}`
 * with `event_type` INSERT | UPDATE | DELETE (one notification) or SYNC (a bulk
 * change — mark-all-read, delete-all-read — without a notification; re-fetch).
 */

import { WebPubSubClient, WebPubSubJsonReliableProtocol } from '@azure/web-pubsub-client'
import { authService, getAccessToken } from './auth-service'
import type { Notification } from './notification-service'
import { env } from '@/lib/env'
import { logInfo, logWarn } from '@/lib/logger'

const CONTEXT = 'NotificationRealtime'

export type NotificationEventType = 'INSERT' | 'UPDATE' | 'DELETE' | 'SYNC'

export interface RealtimeEvent {
  eventType: NotificationEventType
  /** null only for SYNC. */
  notification: Notification | null
  reason?: string
}

export interface NegotiateResponse {
  /** Signed Web PubSub URL — the access token is part of it. */
  url: string
}

export interface RealtimeHandlers {
  onEvent: (event: RealtimeEvent) => void
  /** Every connect and reconnect: the moment to re-sync. */
  onConnected?: () => void
}

/** What a transport reports to the manager. */
export interface TransportEvents {
  connected: () => void
  message: (raw: unknown) => void
  /** The transport gave up (no more automatic reconnects). */
  stopped: () => void
}

export interface RealtimeTransport {
  start: () => Promise<void>
  stop: () => Promise<void>
}

export interface RealtimeDeps {
  negotiate: () => Promise<NegotiateResponse>
  createWebPubSub: (
    first: NegotiateResponse,
    renegotiate: () => Promise<NegotiateResponse>,
    events: TransportEvents,
  ) => RealtimeTransport
}

const EVENT_TYPES: ReadonlySet<string> = new Set(['INSERT', 'UPDATE', 'DELETE', 'SYNC'])
const RETRY_BASE_MS = 2_000
const RETRY_MAX_MS = 60_000

/**
 * One pushed message, validated — anything that does not match the contract
 * is dropped rather than half-applied.
 */
export function parseRealtimeMessage(raw: unknown): RealtimeEvent | null {
  if (!raw || typeof raw !== 'object') return null
  const { event_type: eventType, notification, reason } = raw as Record<string, unknown>
  if (typeof eventType !== 'string' || !EVENT_TYPES.has(eventType)) return null
  if (eventType === 'SYNC') {
    return {
      eventType: 'SYNC',
      notification: null,
      ...(typeof reason === 'string' ? { reason } : {}),
    }
  }
  if (!notification || typeof notification !== 'object') return null
  if (typeof (notification as { id?: unknown }).id !== 'string') return null
  return { eventType: eventType as NotificationEventType, notification: notification as Notification }
}

/** Ask the BFF how to connect; a fresh token on every call (reconnects too). */
export async function negotiateRealtime(): Promise<NegotiateResponse> {
  const token = getAccessToken() ?? (await authService.refreshAccessToken())
  if (!token) throw new Error('Not signed in')
  const response = await fetch(env.realtimeNegotiateUrl, {
    method: 'GET',
    credentials: 'include',
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!response.ok) {
    throw new Error(`Realtime negotiate failed: ${response.status}`)
  }
  const body = (await response.json()) as Partial<NegotiateResponse>
  if (typeof body.url !== 'string' || !body.url) {
    throw new Error('Realtime negotiate returned no URL')
  }
  return { url: body.url }
}

export function createWebPubSubTransport(
  first: NegotiateResponse,
  renegotiate: () => Promise<NegotiateResponse>,
  events: TransportEvents,
): RealtimeTransport {
  let pendingUrl: string | null = first.url
  let stopping = false
  const client = new WebPubSubClient(
    {
      // Called for the first connect and for every reconnect that cannot
      // recover the old connection — a new signed URL each time.
      getClientAccessUrl: async () => {
        if (pendingUrl) {
          const url = pendingUrl
          pendingUrl = null
          return url
        }
        return (await renegotiate()).url
      },
    },
    {
      protocol: WebPubSubJsonReliableProtocol(),
      autoReconnect: true,
      autoRejoinGroups: false,
    },
  )
  client.on('connected', () => events.connected())
  client.on('server-message', (e) => events.message(e.message.data))
  client.on('stopped', () => {
    if (!stopping) events.stopped()
  })
  return {
    start: () => client.start(),
    async stop() {
      stopping = true
      client.stop()
    },
  }
}

const defaultDeps: RealtimeDeps = {
  negotiate: negotiateRealtime,
  createWebPubSub: createWebPubSubTransport,
}

/**
 * One connection per tab, shared by every subscriber. Connects on the first
 * subscription, disconnects after the last one; a failed start or a transport
 * that gave up is retried with capped exponential backoff, and immediately
 * when the browser comes back online.
 */
export class NotificationRealtime {
  private readonly handlers = new Map<string, RealtimeHandlers>()
  private transport: RealtimeTransport | null = null
  private starting: Promise<void> | null = null
  private retryTimer: ReturnType<typeof setTimeout> | null = null
  private retryAttempt = 0
  private nextId = 0
  private onlineListener: (() => void) | null = null

  constructor(private readonly deps: RealtimeDeps = defaultDeps) {}

  async subscribe(handlers: RealtimeHandlers): Promise<string> {
    const id = `sub-${++this.nextId}`
    this.handlers.set(id, handlers)
    this.listenForOnline()
    if (this.transport) {
      // Already connected: this subscriber missed the connect, so it re-syncs now.
      handlers.onConnected?.()
    } else {
      await this.ensureStarted()
    }
    return id
  }

  async unsubscribe(id: string): Promise<void> {
    this.handlers.delete(id)
    if (this.handlers.size === 0) await this.stop()
  }

  get isConnected(): boolean {
    return this.transport !== null
  }

  async stop(): Promise<void> {
    this.clearRetry()
    if (this.onlineListener && typeof window !== 'undefined') {
      window.removeEventListener('online', this.onlineListener)
      this.onlineListener = null
    }
    const transport = this.transport
    this.transport = null
    if (transport) {
      try {
        await transport.stop()
      } catch (error) {
        logWarn({ context: CONTEXT }, `Stopping the realtime connection failed: ${String(error)}`)
      }
    }
  }

  private ensureStarted(): Promise<void> {
    if (this.transport) return Promise.resolve()
    if (!this.starting) {
      this.starting = this.start().finally(() => {
        this.starting = null
      })
    }
    return this.starting
  }

  private async start(): Promise<void> {
    this.clearRetry()
    let transport: RealtimeTransport | null = null
    try {
      const first = await this.deps.negotiate()
      transport = this.deps.createWebPubSub(first, this.deps.negotiate, this.transportEvents(() => transport))
      this.transport = transport
      await transport.start()
      this.retryAttempt = 0
      logInfo({ context: CONTEXT }, 'Realtime connected')
    } catch (error) {
      if (transport && this.transport === transport) {
        this.transport = null
      }
      // Realtime is an accelerator: the list still loads over REST, so a
      // failure is logged and retried, not shown.
      logWarn({ context: CONTEXT }, `Realtime connect failed: ${String(error)}`)
      this.scheduleRetry()
    }
  }

  private transportEvents(self: () => RealtimeTransport | null): TransportEvents {
    const isCurrent = () => self() !== null && self() === this.transport
    return {
      connected: () => {
        if (!isCurrent()) return
        this.handlers.forEach((h) => h.onConnected?.())
      },
      message: (raw) => {
        if (!isCurrent()) return
        const event = parseRealtimeMessage(raw)
        if (!event) {
          logWarn({ context: CONTEXT }, 'Dropped a realtime message outside the contract')
          return
        }
        this.handlers.forEach((h) => h.onEvent(event))
      },
      stopped: () => {
        if (!isCurrent()) return
        this.transport = null
        this.scheduleRetry()
      },
    }
  }

  private scheduleRetry(): void {
    if (this.handlers.size === 0 || this.retryTimer) return
    const delay = Math.min(RETRY_BASE_MS * 2 ** this.retryAttempt, RETRY_MAX_MS)
    this.retryAttempt += 1
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null
      void this.ensureStarted()
    }, delay)
  }

  private clearRetry(): void {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer)
      this.retryTimer = null
    }
  }

  private listenForOnline(): void {
    if (this.onlineListener || typeof window === 'undefined') return
    this.onlineListener = () => {
      if (this.transport || this.handlers.size === 0) return
      this.retryAttempt = 0
      void this.ensureStarted()
    }
    window.addEventListener('online', this.onlineListener)
  }
}

export const notificationRealtime = new NotificationRealtime()
